import React, { useState } from 'react';
import { Row, Col, Card, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHeart, 
  faStar, 
  faEye,
  faMapMarkerAlt,
  faBed,
  faBath,
  faRuler
} from '@fortawesome/free-solid-svg-icons';
import PropertySearch from './PropertySearch';
import PropertySearchLive from './PropertySearchLive';
import PropertySearchEnhanced from './PropertySearchEnhanced';

interface ClientMLSDashboardProps {
  activeTab: string;
  onTabChange: (tabId: string) => void;
}

interface Property {
  id: number;
  image: string;
  price: number;
  beds: number;
  baths: number;
  sqft: number;
  address: string;
  city: string;
  state: string;
  matchScore?: number;
  daysOnMarket: number;
  type: string;
  features: string[];
}

const ClientMLSDashboard: React.FC<ClientMLSDashboardProps> = ({
  activeTab,
  onTabChange
}) => {
  const [recommendedProperties] = useState<Property[]>([
    {
      id: 1,
      image: '/api/placeholder/300/200',
      price: 425000,
      beds: 3,
      baths: 2,
      sqft: 1850,
      address: '123 Desert View Dr',
      city: 'Tempe',
      state: 'AZ',
      matchScore: 95,
      daysOnMarket: 12,
      type: 'Single Family',
      features: ['Pool', 'Updated Kitchen', 'Mountain Views']
    },
    {
      id: 2,
      image: '/api/placeholder/300/200',
      price: 389900,
      beds: 3,
      baths: 2.5,
      sqft: 1680,
      address: '456 Palm Springs Ave',
      city: 'Chandler',
      state: 'AZ',
      matchScore: 92,
      daysOnMarket: 8,
      type: 'Townhome',
      features: ['Garage', 'Community Pool', 'Near Schools']
    },
    {
      id: 3,
      image: '/api/placeholder/300/200',
      price: 445000,
      beds: 4,
      baths: 3,
      sqft: 2100,
      address: '789 Cactus Ridge Way',
      city: 'Gilbert',
      state: 'AZ',
      matchScore: 89,
      daysOnMarket: 25,
      type: 'Single Family',
      features: ['Large Lot', 'RV Parking', 'Upgraded Flooring']
    }
  ]);

  const [savedProperties] = useState<Property[]>([
    {
      id: 4,
      image: '/api/placeholder/300/200',
      price: 375000,
      beds: 2,
      baths: 2,
      sqft: 1200,
      address: '321 Urban Loft St',
      city: 'Phoenix',
      state: 'AZ',
      daysOnMarket: 15,
      type: 'Condo',
      features: ['Downtown', 'High Rise', 'City Views']
    }
  ]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  const PropertyCard: React.FC<{ property: Property; showMatch?: boolean }> = ({ 
    property, 
    showMatch = false 
  }) => (
    <Card className="property-card mb-3 h-100">
      <div className="position-relative">
        <Card.Img 
          variant="top" 
          src={property.image} 
          style={{ height: '200px', objectFit: 'cover' }}
          onError={(e) => {
            (e.target as HTMLImageElement).src = `https://via.placeholder.com/300x200/e9ecef/6c757d?text=Property+Image`;
          }}
        />
        {showMatch && property.matchScore && (
          <Badge 
            bg="success" 
            className="position-absolute top-0 start-0 m-2"
          >
            {property.matchScore}% Match
          </Badge>
        )}
        <div className="position-absolute top-0 end-0 m-2">
          <FontAwesomeIcon 
            icon={faHeart} 
            className="text-white bg-dark rounded-circle p-2 cursor-pointer" 
            style={{ fontSize: '1.2rem' }}
          />
        </div>
      </div>
      <Card.Body className="d-flex flex-column">
        <div className="mb-2">
          <h5 className="card-title text-primary mb-1">
            {formatPrice(property.price)}
          </h5>
          <p className="text-muted mb-2">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
            {property.address}, {property.city}, {property.state}
          </p>
        </div>
        
        <div className="property-details mb-3">
          <Row className="g-3">
            <Col xs={4} className="text-center">
              <FontAwesomeIcon icon={faBed} className="text-muted" />
              <div className="small">{property.beds} BR</div>
            </Col>
            <Col xs={4} className="text-center">
              <FontAwesomeIcon icon={faBath} className="text-muted" />
              <div className="small">{property.baths} BA</div>
            </Col>
            <Col xs={4} className="text-center">
              <FontAwesomeIcon icon={faRuler} className="text-muted" />
              <div className="small">{property.sqft?.toLocaleString()} sqft</div>
            </Col>
          </Row>
        </div>

        <div className="mt-auto">
          {property.features && property.features.length > 0 && (
            <div className="mb-2">
              {property.features.slice(0, 2).map((feature, idx) => (
                <Badge key={idx} bg="light" text="dark" className="me-1 mb-1">
                  {feature}
                </Badge>
              ))}
              {property.features.length > 2 && (
                <Badge bg="light" text="dark">
                  +{property.features.length - 2} more
                </Badge>
              )}
            </div>
          )}
          
          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">
              {property.daysOnMarket} days on market
            </small>
            <div>
              <button className="btn btn-outline-primary btn-sm me-2">
                <FontAwesomeIcon icon={faEye} className="me-1" />
                View
              </button>
              <button className="btn btn-primary btn-sm">
                Details
              </button>
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  const renderRecommendations = () => (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>⭐ Recommended for You</h4>
        <Badge bg="info">AI Powered</Badge>
      </div>
      <Row>
        {recommendedProperties.map((property) => (
          <Col key={property.id} lg={4} md={6} className="mb-4">
            <PropertyCard property={property} showMatch={true} />
          </Col>
        ))}
      </Row>
    </div>
  );

  const renderSavedProperties = () => (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>❤️ Saved Properties</h4>
        <Badge bg="secondary">{savedProperties.length} saved</Badge>
      </div>
      {savedProperties.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <FontAwesomeIcon icon={faHeart} size="3x" className="text-muted mb-3" />
            <h5>No Saved Properties</h5>
            <p className="text-muted">Start saving properties you love to see them here!</p>
            <button 
              className="btn btn-primary"
              onClick={() => onTabChange('search')}
            >
              Start Searching
            </button>
          </Card.Body>
        </Card>
      ) : (
        <Row>
          {savedProperties.map((property) => (
            <Col key={property.id} lg={4} md={6} className="mb-4">
              <PropertyCard property={property} />
            </Col>
          ))}
        </Row>
      )}
    </div>
  );

  const renderVirtualTours = () => (
    <Card>
      <Card.Body className="text-center py-5">
        <FontAwesomeIcon icon={faEye} size="3x" className="text-muted mb-3" />
        <h5>Virtual Tours</h5>
        <p className="text-muted mb-3">
          Experience properties from the comfort of your home with immersive virtual tours.
        </p>
        <button className="btn btn-outline-primary">
          Coming Soon
        </button>
      </Card.Body>
    </Card>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'search':
        return <PropertySearchEnhanced isAgent={false} />;
      case 'recommendations':
        return renderRecommendations();
      case 'saved':
        return renderSavedProperties();
      case 'tours':
        return renderVirtualTours();
      default:
        return <PropertySearchEnhanced isAgent={false} />;
    }
  };

  return (
    <div className="client-mls-dashboard">
      {renderContent()}
    </div>
  );
};

export default ClientMLSDashboard;